import { useEffect, useCallback } from 'react'

// Default pixel used across the site (and on /demo).
const PIXEL_ID = '2826493141057409'
// Per-path pixel overrides. Any route listed here initializes + fires to its
// own pixel/dataset instead of the default one. /paso1 uses a distinct pixel
// from /demo. Leave a value empty ('') to fall back to the default PIXEL_ID.
const PIXEL_BY_PATH: Record<string, string> = {
  // TODO: replace with the real /paso1 pixel id. Until set, /paso1 falls back
  // to the default pixel.
  '/paso1': '',
}
// Resolve which pixel id applies to the CURRENT route (falls back to default).
export function getActivePixelId(): string {
  if (typeof window === 'undefined') return PIXEL_ID
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  const override = PIXEL_BY_PATH[path]
  return override ? override : PIXEL_ID
}
// Dedicated dataset for CTA-click conversions (PageView / ViewContent /
// AddToCart fired when a visitor clicks a CTA button). Initialized alongside
// the primary pixel so events can be routed to it with `trackSingle`.
export const CTA_PIXEL_ID = '2835854783427335'
// Per-path override for the CTA-click datasets. A route may fire its CTA
// conversions to SEVERAL datasets at once — every id listed here receives
// PageView / ViewContent / AddToCart on a CTA click. Any route not listed
// falls back to CTA_PIXEL_ID.
const DEMO_DATASETS = [
  '1272549114780310',
  '754551913878347',
  '2842475396100201',
]
const CTA_PIXELS_BY_PATH: Record<string, string[]> = {
  '/demo': DEMO_DATASETS,
  // Duplicates of the /demo lander — same offer, same datasets.
  '/demo-p': DEMO_DATASETS,
  '/paso1': DEMO_DATASETS,
  '/vsl1': DEMO_DATASETS,
  // The quiz funnels into /demo, so it reports to the same datasets.
  '/quiz': DEMO_DATASETS,
  '/PD1': DEMO_DATASETS,
  '/pd1': DEMO_DATASETS,
  '/yt': ['1272549114780310'],
}
// Resolve which CTA dataset(s) apply to a GIVEN route. Useful when a page is
// embedded in another (e.g. the offer iframe inside /demo) and must report to
// the HOST page's datasets rather than its own.
export function getCtaPixelIdsForPath(path: string): string[] {
  const clean = path.replace(/\/+$/, '') || '/'
  return CTA_PIXELS_BY_PATH[clean] || [CTA_PIXEL_ID]
}
// Resolve which CTA dataset(s) apply to the CURRENT route.
export function getActiveCtaPixelIds(): string[] {
  if (typeof window === 'undefined') return [CTA_PIXEL_ID]
  return getCtaPixelIdsForPath(window.location.pathname)
}

// Extend window type for fbq
declare global {
  interface Window {
    fbq: any
    _fbq: any
  }
}

/**
 * Inject the Meta Pixel, init it, and fire PageView — exactly once.
 * Exported so it can be called at BUNDLE LOAD time (in index.tsx, before React
 * renders) instead of waiting for a component effect. Firing this early is the
 * single biggest lever for capturing Landing Page Views from slow-mobile
 * visitors who would otherwise bounce before the React app mounts.
 * Idempotent: the `window.fbq` guard means calling it again is a no-op.
 */
export function ensureMetaPixel() {
  if (typeof window === 'undefined' || window.fbq) return
  const f = window
  const b = document
  const e = 'script'

  const n: any = (f.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  })

  if (!f._fbq) f._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const t = b.createElement(e) as HTMLScriptElement
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'

  const s = b.getElementsByTagName(e)[0]
  s?.parentNode?.insertBefore(t, s)

  // Initialize the pixel that applies to the current route (default, or a
  // per-path override such as a distinct /paso1 pixel).
  window.fbq('init', getActivePixelId())
  // Also initialize every CTA-conversion dataset for the current route so
  // CTA-click events can be routed to them via `trackSingle`.
  for (const ctaId of getActiveCtaPixelIds()) {
    if (ctaId !== getActivePixelId()) window.fbq('init', ctaId)
  }
  window.fbq('track', 'PageView')
}

export function useMetaPixel() {
  useEffect(() => {
    // Safety net: ensure the pixel exists even if early init was skipped.
    ensureMetaPixel()
  }, [])

  const trackCustomEvent = useCallback((eventName: string, params?: object) => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', eventName, params)
    }
  }, [])

  const trackStandardEvent = useCallback(
    (eventName: string, params?: object) => {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', eventName, params)
      }
    },
    [],
  )

  // Fire a standard event to ONE specific pixel/dataset (via `trackSingle`)
  // instead of all initialized pixels. Used to route CTA-click conversions to
  // the dedicated CTA dataset.
  const trackStandardEventTo = useCallback(
    (pixelId: string, eventName: string, params?: object) => {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackSingle', pixelId, eventName, params)
      }
    },
    [],
  )

  // Fire a CUSTOM event to ONE specific pixel/dataset (via `trackSingleCustom`).
  const trackCustomEventTo = useCallback(
    (pixelId: string, eventName: string, params?: object) => {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackSingleCustom', pixelId, eventName, params)
      }
    },
    [],
  )

  // Initialize extra datasets for THIS document. Needed when a page has to
  // report to datasets that its own route doesn't map to — e.g. the offer
  // iframe embedded in /demo, which must report to the host page's datasets.
  const initPixels = useCallback((pixelIds: string[]) => {
    if (typeof window === 'undefined') return
    ensureMetaPixel()
    if (!window.fbq) return
    for (const id of pixelIds) window.fbq('init', id)
  }, [])

  return {
    trackCustomEvent,
    trackStandardEvent,
    trackStandardEventTo,
    trackCustomEventTo,
    initPixels,
  }
}
